import { createApi } from "@reduxjs/toolkit/query/react";
import { components, operations } from "../../types/api";
import { baseQueryWithReauth } from "../../utils/baseQuery";

// Data types from the API schema
type PublisherMetricsResponse =
  components["schemas"]["PublisherMetricsResponse"];

// Response type
type ApiResponsePublisherMetricsResponse =
  components["schemas"]["ApiResponse_PublisherMetricsResponse_"];

// Parameter types from operations
type GetPublisherMetricsParams =
  operations["GetPublisherMetrics"]["parameters"];

export const publisherMetricsApi = createApi({
  reducerPath: "publisherMetricsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["PublisherMetrics"],
  endpoints: (builder) => ({
    // Get publisher metrics for a specific internal campaign
    getPublisherMetrics: builder.query<
      PublisherMetricsResponse,
      GetPublisherMetricsParams
    >({
      query: (params) => {
        // Extract path parameters
        const internalCampaignId = params.path.internalCampaignId;
        let queryString = `/publisher-metrics/${internalCampaignId}`;

        // Handle optional query parameters
        if (params.query) {
          const urlParams = new URLSearchParams();

          if (params.query.startDate) {
            urlParams.append("startDate", params.query.startDate);
          }

          if (params.query.endDate) {
            urlParams.append("endDate", params.query.endDate);
          }

          const queryParams = urlParams.toString();
          if (queryParams) queryString += `?${queryParams}`;
        }

        return queryString;
      },
      transformResponse: (response: ApiResponsePublisherMetricsResponse) =>
        response.data as PublisherMetricsResponse,
      providesTags: ["PublisherMetrics"],
    }),
    getMergedPublisherMetrics: builder.query<
      PublisherMetricsResponse,
      operations["GetMergedPublisherMetrics"]["parameters"]
    >({
      query: (params) => {
        // Extract path parameters
        const internalCampaignMergeId = params.path.internalCampaignMergeId;
        let queryString = `/publisher-metrics/merged/${internalCampaignMergeId}`;

        // Handle optional query parameters
        if (params.query) {
          const urlParams = new URLSearchParams();

          const queryParams = urlParams.toString();
          if (queryParams) queryString += `?${queryParams}`;
        }

        return queryString;
      },
      transformResponse: (response: ApiResponsePublisherMetricsResponse) =>
        response.data as PublisherMetricsResponse,
      providesTags: ["PublisherMetrics"],
    }),
  }),
});

export const {
  useGetPublisherMetricsQuery,
  useGetMergedPublisherMetricsQuery,
} = publisherMetricsApi;
